import { useLocalization } from '@ais-ui/locale-management';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DatePicker,
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { z } from 'zod';
import { GridJobConfigSchemaOutput } from '@ais-ui/api-spec/gpslost';
import { ShapeSelect } from '../../Shapes/ShapeSelects';
import { useStatVoyPosReportsDaterange } from '@ais-ui/api';
import { useLengthUnits } from '../../Units/useLengthUnits';
import { Units } from '@ais-ui/utils';
import { isUpdateIntervalDivisible } from './gpsLostValidation';

const gpsLostFormSchema = z
  .object({
    name: z.string().min(1, { message: 'Name is required' }),
    updateIntervalHours: z.number(),
    historicalRangeLowerBound: z.date(),
    historicalRangeUpperBound: z.date(),
    shapeId: z.string().min(1, 'Shape is required'),
    enabled: z.boolean(),
    gridCellSizeMeters: z.number(),
  })
  .refine(
    (data) => data.historicalRangeLowerBound < data.historicalRangeUpperBound,
    {
      message: 'Start date must be before end date',
      path: ['historicalRangeLowerBound'], // This targets the error to the lower bound field
    },
  )
  .refine(
    (data) =>
      isUpdateIntervalDivisible(
        data.historicalRangeLowerBound,
        data.historicalRangeUpperBound,
        data.updateIntervalHours,
      ),
    {
      message:
        'Update interval must divide evenly into the total duration of the historical range',
      path: ['updateIntervalHours'],
    },
  );

export type GPSLostFormValues = z.infer<typeof gpsLostFormSchema>;

interface SaveGPSLostDialogProps {
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
  action: 'edit' | 'create';
  mutate: any;
  gpsLostConfiguration?: GridJobConfigSchemaOutput;
}

interface SaveGPSLostFormProps {
  form: UseFormReturn<GPSLostFormValues>;
}

const SaveGPSLostForm = ({ form }: SaveGPSLostFormProps) => {
  const { messages } = useLocalization();
  const { selectedUnit } = useLengthUnits();
  const {
    data: availableDaterange,
    isLoading: isLoadingDaterange,
    isError: isErrorDaterange,
  } = useStatVoyPosReportsDaterange();

  const isEnabled = form.watch('enabled');

  // Get grid cell size options based on user's unit preference
  const gridCellOptions = useMemo(() => {
    switch (selectedUnit) {
      case Units.IMPERIAL:
        return [
          { meters: 10000, display: '6.2 mi', value: '10000' },
          { meters: 25000, display: '15.5 mi', value: '25000' },
          { meters: 50000, display: '31.1 mi', value: '50000' },
          { meters: 100000, display: '62.1 mi', value: '100000' },
        ];
      case Units.MARITIME:
        return [
          { meters: 10000, display: '5.4 NM', value: '10000' },
          { meters: 25000, display: '13.5 NM', value: '25000' },
          { meters: 50000, display: '27.0 NM', value: '50000' },
          { meters: 100000, display: '54.0 NM', value: '100000' },
        ];
      case Units.METRIC:
      default:
        return [
          { meters: 10000, display: '10 km', value: '10000' },
          { meters: 25000, display: '25 km', value: '25000' },
          { meters: 50000, display: '50 km', value: '50000' },
          { meters: 100000, display: '100 km', value: '100000' },
        ];
    }
  }, [selectedUnit]);

  // Update the upper bound to today whenever enabled is set to true
  useEffect(() => {
    if (isEnabled) {
      form.setValue('historicalRangeUpperBound', new Date());
    }
  }, [isEnabled, form]);

  const disableDates = (date: Date) => {
    // Disable future dates
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set time to the end of today

    if (date > today) {
      return true; // Disable dates after today
    }

    // Disable dates outside of available date range if we have that data
    if (availableDaterange) {
      const lowerBound = new Date(availableDaterange.lower);
      const upperBound = new Date(availableDaterange.upper);

      // Remove time component for comparison
      lowerBound.setHours(0, 0, 0, 0);
      upperBound.setHours(23, 59, 59, 999);

      // Create a copy of the date with zeroed time for comparison
      const dateToCompare = new Date(date);
      dateToCompare.setHours(0, 0, 0, 0);

      return dateToCompare < lowerBound || dateToCompare > upperBound;
    }

    return false;
  };

  return (
    <div className="mt-8 space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.filtersCreateNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input id="filter-name" type="text" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="enabled"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Enabled</FormLabel>
              <InfoIconTooltip>
                When enabled the configuration will track GPS lost over time
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Switch
                checked={field.value}
                onCheckedChange={(value) => {
                  field.onChange(value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Historical range</Label>

          <InfoIconTooltip>
            Select the historical time period for calculating GPS lost
            retroactively
          </InfoIconTooltip>
        </div>

        <div className="flex w-full space-x-4">
          <FormField
            control={form.control}
            name="historicalRangeLowerBound"
            render={({ field }) => (
              <FormItem className="w-full">
                <div className="flex justify-between">
                  <FormLabel className="font-normal">Lower</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disabledCalendar={disableDates}
                  ></DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="historicalRangeUpperBound"
            render={({ field }) => (
              <FormItem className="w-full">
                <div className="flex justify-between">
                  <FormLabel className="font-normal">Upper</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isEnabled}
                    disabledCalendar={disableDates}
                  ></DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <FormField
        control={form.control}
        name="updateIntervalHours"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Update interval</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                value={field.value?.toString()}
                onValueChange={(value) => {
                  field.onChange(parseInt(value, 10));
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Select hours" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="3">3 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Grid</Label>
          <InfoIconTooltip>
            Choose area and resolution (cell size) for GPS lost analysis. The
            cell size is the diameter of the grid cells.
          </InfoIconTooltip>
        </div>

        <div className="flex w-full space-x-4">
          <FormField
            control={form.control}
            name="shapeId"
            render={({ field }) => (
              <FormItem className="w-full">
                <div className="flex justify-between">
                  <FormLabel className="font-normal">Shape *</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <ShapeSelect
                    className="w-72"
                    id="gps-lost-shape-select"
                    value={field.value}
                    onChange={field.onChange}
                    allowedGeometryTypes={['Polygon']}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gridCellSizeMeters"
            render={({ field }) => (
              <FormItem className="w-full">
                <div className="flex justify-between">
                  <FormLabel className="font-normal">Grid Cell Size</FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Select
                    value={field.value?.toString()}
                    onValueChange={(value) => {
                      field.onChange(parseInt(value, 10));
                    }}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {gridCellOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export function SaveGPSLostDialog(props: SaveGPSLostDialogProps) {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<GPSLostFormValues>({
    resolver: zodResolver(gpsLostFormSchema),
    defaultValues: {
      name: props.gpsLostConfiguration?.jobName || '',
      updateIntervalHours:
        props.gpsLostConfiguration?.updateIntervalHours || 24,
      historicalRangeLowerBound:
        props.gpsLostConfiguration?.historicalRange?.lower ||
        new Date(Date.now() - 24 * 60 * 60 * 1000),
      historicalRangeUpperBound:
        props.gpsLostConfiguration?.historicalRange?.upper || new Date(),
      shapeId: props.gpsLostConfiguration?.shapeUuid || '',
      enabled: props.gpsLostConfiguration?.enabled || false,
      gridCellSizeMeters:
        props.gpsLostConfiguration?.gridCellSizeMeters || 10000,
    },
  });

  const { toast } = useToast();

  const [dialogTitle, dialogDescription] =
    props.action === 'edit'
      ? [messages.gpsLostEditTitle, messages.gpsLostEditDescription]
      : [messages.gpsLostCreateTitle, messages.gpsLostCreateDescription];

  const onSubmit = async (data: GPSLostFormValues) => {
    setIsSubmitting(true);

    const upperBoundDate = data.enabled
      ? new Date()
      : data.historicalRangeUpperBound;

    const formatDateLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const gpsLostConfigurationToSave: GridJobConfigSchemaOutput = {
      jobName: data.name,
      updateIntervalHours: data.updateIntervalHours,
      historicalRange: {
        lower: formatDateLocal(data.historicalRangeLowerBound),
        upper: formatDateLocal(upperBoundDate),
      },
      shapeUuid: data.shapeId,
      enabled: data.enabled,
      gridCellSizeMeters: data.gridCellSizeMeters,
    };

    const callbacks = mutateCallbacks(
      toast,
      messages,
      props.action,
      'gps lost',
      setIsSubmitting,
      props.setShowDialog,
    );

    if (props.action === 'edit') {
      const id = props.gpsLostConfiguration?.id;
      gpsLostConfigurationToSave.id = id;
      if (!id) {
        toast({
          className: 'save-gps-lost-error-toast',
          title: 'Error!',
          description: 'Missing ID for gps lost configuration',
          variant: 'destructive',
        });
        props.setShowDialog(false);
        return;
      }
    }
    props.mutate(gpsLostConfigurationToSave, callbacks);
  };

  return (
    <Form {...form}>
      <DialogForm
        showDialog={props.showDialog}
        onOpenChange={props.setShowDialog}
        size="md"
      >
        <DialogForm.Header>
          <DialogForm.Title>{dialogTitle}</DialogForm.Title>
          <DialogForm.Description>{dialogDescription}</DialogForm.Description>
        </DialogForm.Header>
        <DialogForm.Body observeResize={false}>
          <SaveGPSLostForm form={form} />
        </DialogForm.Body>
        <DialogForm.Footer>
          <DialogForm.Cancel>{messages.genericCancel}</DialogForm.Cancel>
          <DialogForm.Submit
            id="gps-lost-submit-button"
            disabled={isSubmitting}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </DialogForm.Submit>
        </DialogForm.Footer>
      </DialogForm>
    </Form>
  );
}
